import { supabase } from '../lib/supabase';

/**
 * Service for calling the Supabase Edge Function to evaluate drills with Lumo AI
 * Handles HTTP requests to the evaluate-drill Edge Function
 */
export const lumoEdgeFunctionService = {
  /**
   * Call the Lumo AI evaluation Edge Function
   * @param {Object} evaluationData - Data for evaluation
   * @param {string} evaluationData.userId - Current authenticated user ID
   * @param {string} evaluationData.userDrillId - ID of the user_drills record
   * @param {string} evaluationData.drillId - ID of the drill being evaluated
   * @param {string} evaluationData.userResponseText - User's response to the drill
   * @returns {Promise<Object>} Evaluation results from Lumo AI
   */
  async evaluateDrillWithLumo(evaluationData) {
    try {
      // Get current Supabase URL from environment or client
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      
      if (!supabaseUrl) {
        throw new Error('Supabase URL not configured. Please check your .env file.');
      }

      // Construct Edge Function URL
      const edgeFunctionUrl = `${supabaseUrl}/functions/v1/evaluate-drill`;

      // Get current session for authorization
      const { data: { session } } = await supabase?.auth?.getSession();
      
      if (!session) {
        throw new Error('No active session. Please login again.');
      }

      console.log('Calling Edge Function:', edgeFunctionUrl);
      console.log('Request payload:', {
        user_id: evaluationData?.userId,
        user_drill_id: evaluationData?.userDrillId,
        drill_id: evaluationData?.drillId,
        user_response_length: evaluationData?.userResponseText?.length
      });

      // Make HTTP POST request to Edge Function with proper error handling
      let response;
      try {
        response = await fetch(edgeFunctionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json','Authorization': `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({
            user_id: evaluationData?.userId,
            user_drill_id: evaluationData?.userDrillId,
            drill_id: evaluationData?.drillId,
            user_response_text: evaluationData?.userResponseText
          })
        });
      } catch (fetchError) {
        console.error('Network error when calling Edge Function:', fetchError);
        throw new Error(
          'Unable to reach the Lumo AI service. This could mean:\n' +'1. The Edge Function "evaluate-drill" has not been deployed to your Supabase project\n' +'2. There is a network connectivity issue\n' +'3. CORS is blocking the request\n\n' +'Please ensure the Edge Function is deployed and running in your Supabase project.'
        );
      }

      // Check if request was successful
      if (!response?.ok) {
        let errorMessage = `Edge Function returned ${response?.status}: ${response?.statusText}`;
        
        try {
          const errorData = await response?.json();
          if (errorData?.error || errorData?.message) {
            errorMessage = errorData?.error || errorData?.message;
          }
        } catch (jsonError) {
          // If response is not JSON, try to get text
          try {
            const errorText = await response?.text();
            if (errorText) {
              errorMessage = `Edge Function error: ${errorText}`;
            }
          } catch (textError) {
            // Keep the default error message
          }
        }
        
        console.error('Edge Function error response:', errorMessage);
        throw new Error(errorMessage);
      }

      // Parse and return the response
      const result = await response?.json();
      
      console.log('Edge Function response:', result);

      return {
        success: true,
        data: {
          overallScore: result?.overall_score,
          clarityScore: result?.clarity_score,
          confidenceScore: result?.confidence_score,
          controlScore: result?.control_score,
          closeScore: result?.close_score,
          feedbackText: result?.feedback_text,
          suggestedScript: result?.suggested_script
        }
      };
    } catch (error) {
      console.error('Error calling Lumo Edge Function:', error);
      
      // Return structured error with helpful information
      return {
        success: false,
        error: {
          message: error?.message || 'Failed to evaluate drill with Lumo AI',
          details: error?.toString(),
          timestamp: new Date()?.toISOString(),
          helpText: 'Please check the browser console for more details and ensure the Edge Function is deployed.'
        }
      };
    }
  },

  /**
   * Call the Lumo AI objection evaluation Edge Function
   * @param {Object} evaluationData - Data for evaluation
   * @param {string} evaluationData.userId - Current authenticated user ID
   * @param {string} evaluationData.objectionId - ID of the objection being evaluated
   * @param {string} evaluationData.userResponseText - User's response to the objection
   * @returns {Promise<Object>} Evaluation results from Lumo AI
   */
  async evaluateObjectionWithLumo(evaluationData) {
    try {
      // Get current Supabase URL from environment or client
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      
      if (!supabaseUrl) {
        throw new Error('Supabase URL not configured. Please check your .env file.');
      }

      // Construct Edge Function URL
      const edgeFunctionUrl = `${supabaseUrl}/functions/v1/evaluate-objection`;

      // Get current session for authorization
      const { data: { session } } = await supabase?.auth?.getSession();
      
      if (!session) {
        throw new Error('No active session. Please login again.');
      }

      console.log('Calling Objection Evaluation Edge Function:', edgeFunctionUrl);
      console.log('Request payload:', {
        user_id: evaluationData?.userId,
        objection_id: evaluationData?.objectionId,
        user_response_length: evaluationData?.userResponseText?.length
      });

      // Make HTTP POST request to Edge Function with proper error handling
      let response;
      try {
        response = await fetch(edgeFunctionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({
            user_id: evaluationData?.userId,
            objection_id: evaluationData?.objectionId,
            user_response_text: evaluationData?.userResponseText
          })
        });
      } catch (fetchError) {
        console.error('Network error when calling Edge Function:', fetchError);
        throw new Error(
          'Unable to reach the Lumo AI service. This could mean:\n' + '1. The Edge Function "evaluate-objection" has not been deployed to your Supabase project\n'+ '2. There is a network connectivity issue\n'+ '3. CORS is blocking the request\n\n'+ 'Please ensure the Edge Function is deployed and running in your Supabase project.'
        );
      }

      // Check if request was successful
      if (!response?.ok) {
        let errorMessage = `Edge Function returned ${response?.status}: ${response?.statusText}`;
        
        try {
          const errorData = await response?.json();
          if (errorData?.error || errorData?.message) {
            errorMessage = errorData?.error || errorData?.message;
          }
        } catch (jsonError) {
          // If response is not JSON, try to get text
          try {
            const errorText = await response?.text();
            if (errorText) {
              errorMessage = `Edge Function error: ${errorText}`;
            }
          } catch (textError) {
            // Keep the default error message
          }
        }
        
        console.error('Edge Function error response:', errorMessage);
        throw new Error(errorMessage);
      }

      // Parse and return the response
      const result = await response?.json();
      
      console.log('Edge Function response:', result);

      return {
        success: true,
        data: {
          lumoScore: result?.lumo_score,
          feedbackText: result?.lumo_feedback_text,
          suggestedScript: result?.lumo_suggested_script,
          attemptId: result?.attempt_id
        }
      };
    } catch (error) {
      console.error('Error calling Lumo Objection Edge Function:', error);
      
      // Return structured error with helpful information
      return {
        success: false,
        error: {
          message: error?.message || 'Failed to evaluate objection with Lumo AI',
          details: error?.toString(),
          timestamp: new Date()?.toISOString(),
          helpText: 'Please check the browser console for more details and ensure the Edge Function is deployed.'
        }
      };
    }
  },

  /**
   * Update user_drills record with Lumo evaluation results
   * @param {string} userDrillId - ID of user_drills record to update
   * @param {Object} lumoResults - Results from Lumo evaluation
   * @returns {Promise<Object>} Updated user_drills record
   */
  async updateDrillWithLumoResults(userDrillId, lumoResults) {
    try {
      const { data, error } = await supabase
        ?.from('user_drills')
        ?.update({
          lumo_overall_score: lumoResults?.overallScore,
          lumo_clarity_score: lumoResults?.clarityScore,
          lumo_confidence_score: lumoResults?.confidenceScore,
          lumo_control_score: lumoResults?.controlScore,
          lumo_close_score: lumoResults?.closeScore,
          lumo_feedback_text: lumoResults?.feedbackText,
          lumo_suggested_script: lumoResults?.suggestedScript,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', userDrillId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        userId: data?.user_id,
        drillId: data?.drill_id,
        responseText: data?.response_text,
        score: data?.score,
        lumoOverallScore: data?.lumo_overall_score,
        lumoClarityScore: data?.lumo_clarity_score,
        lumoConfidenceScore: data?.lumo_confidence_score,
        lumoControlScore: data?.lumo_control_score,
        lumoCloseScore: data?.lumo_close_score,
        lumoFeedbackText: data?.lumo_feedback_text,
        lumoSuggestedScript: data?.lumo_suggested_script,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating drill with Lumo results:', error);
      throw error;
    }
  }
};